/**
 * Open a print-ready HTML quote using vacation-package (חבילות נופש) PDF design.
 */
import { getUserInfo } from "../services/localStorage";
import {
  HOTELSRIVER_OFFICIAL_LOGO_ABSOLUTE_URL,
  HOTELSRIVER_OFFICIAL_LOGO_URL,
} from "./hotelsRiverBrandLogo";
import {
  QUOTE_PDF_CONTAINER_CLASS,
  createQuotePdfContainerId,
  buildVacationPackageQuotePdfCss,
  buildVacationPackageQuotePdfDocumentCss,
} from "./quotePdfHtml";
import {
  buildVacationPackageQuoteBodyHtml,
  escapeQuotePdfHtml,
} from "./vacationPackageQuotePdfBody";

export async function resolveQuotePdfAgentLogoBase64() {
  const userInfo = getUserInfo();
  const agentLogo = userInfo?.companylogobase65 || null;
  if (agentLogo) return agentLogo;

  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png", 1.0));
        } catch {
          resolve(HOTELSRIVER_OFFICIAL_LOGO_ABSOLUTE_URL);
        }
      };
      img.onerror = () => resolve(HOTELSRIVER_OFFICIAL_LOGO_ABSOLUTE_URL);
      img.src = HOTELSRIVER_OFFICIAL_LOGO_URL;
    });
  } catch {
    return HOTELSRIVER_OFFICIAL_LOGO_ABSOLUTE_URL;
  }
}

/**
 * @param {object} bodyOpts — passed to buildVacationPackageQuoteBodyHtml (logoBase64 optional)
 * @param {{ titleSuffix?: string, fileSlug?: string, containerIdPrefix?: string }} [meta]
 * @returns {Promise<{ opened: boolean, downloaded?: boolean }>}
 */
export async function openVacationPackageStyleQuotePdf(bodyOpts = {}, meta = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { opened: false };
  }

  const logoBase64 = bodyOpts.logoBase64 || (await resolveQuotePdfAgentLogoBase64());
  const titleSuffix = meta.titleSuffix || "quote";
  const fileSlug =
    (meta.fileSlug || titleSuffix)
      .replace(/[^a-zA-Z0-9._\u0590-\u05FF-]+/g, "_")
      .slice(0, 64) || "quote";
  const containerId = createQuotePdfContainerId(meta.containerIdPrefix || "pdf-quote");
  const scopedCss = buildVacationPackageQuotePdfCss(`#${containerId}`);
  const exportCss = buildVacationPackageQuotePdfCss(`.${QUOTE_PDF_CONTAINER_CLASS}`);

  const pdfContainer = document.createElement("div");
  pdfContainer.id = containerId;
  Object.assign(pdfContainer.style, {
    position: "absolute",
    top: "0",
    left: "0",
    width: "794px",
    backgroundColor: "white",
    fontFamily: '"Segoe UI", Arial, sans-serif',
    direction: "rtl",
    fontSize: "10pt",
    lineHeight: "1.2",
    color: "#000",
    zIndex: "-9999",
    opacity: "0",
    pointerEvents: "none",
    overflow: "visible",
  });

  const styleElement = document.createElement("style");
  styleElement.textContent = scopedCss;
  document.head.appendChild(styleElement);

  pdfContainer.innerHTML = buildVacationPackageQuoteBodyHtml({
    ...bodyOpts,
    logoBase64,
  });
  document.body.appendChild(pdfContainer);

  await new Promise((resolve) => setTimeout(resolve, 200));
  await new Promise((resolve) => {
    const images = pdfContainer.querySelectorAll("img");
    if (images.length === 0) {
      resolve();
      return;
    }
    let loaded = 0;
    const totalImages = images.length;
    const done = () => {
      loaded += 1;
      if (loaded >= totalImages) resolve();
    };
    images.forEach((img) => {
      if (img.complete) {
        done();
        return;
      }
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
    setTimeout(resolve, 10000);
  });

  const fullHTML = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>הצעת מחיר - חבילת נופש - ${escapeQuotePdfHtml(titleSuffix)}</title>
  <style>
${buildVacationPackageQuotePdfDocumentCss(exportCss)}
  </style>
</head>
<body>
  <div class="${QUOTE_PDF_CONTAINER_CLASS}">
    ${pdfContainer.innerHTML}
  </div>
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 500);
    };
  </script>
</body>
</html>`;

  const blob = new Blob([fullHTML], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const newWindow = window.open(url, "_blank");

  try {
    document.body.removeChild(pdfContainer);
    document.head.removeChild(styleElement);
  } catch {
    /* ignore cleanup errors */
  }

  if (newWindow) {
    newWindow.focus();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { opened: true };
  }

  const link = document.createElement("a");
  link.href = url;
  link.download = `הצעת_מחיר_${fileSlug}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { opened: false, downloaded: true };
}
